import { jsPDF } from "jspdf";
import { CVData } from "./../components/CVContent";

const generatePDF = () => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const ML = 15;
  const MR = 15;
  const PAGE_W = 210;
  const PAGE_H = 297;
  const CONTENT_W = PAGE_W - ML - MR;
  const BOTTOM_MARGIN = 20;
  const LINE_H = 6.5;

  let y = 18;

  const checkPage = (needed = 10) => {
    if (y + needed > PAGE_H - BOTTOM_MARGIN) {
      doc.addPage();
      y = 18;
    }
  };

  const setStyle = (size, style, color) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const write = (text, x, options = {}) => {
    const width = options.width || CONTENT_W - (x - ML);
    const lines = doc.splitTextToSize(String(text), width);
    lines.forEach((line) => {
      checkPage(LINE_H);
      doc.text(line, x, y);
      y += LINE_H;
    });
    return y;
  };

  const getWrappedLines = (text, x, options = {}) => {
    const width = options.width || CONTENT_W - (x - ML);
    return doc.splitTextToSize(String(text), width);
  };

  const writeBullet = (text, indent = ML + 7) => {
    const lines = getWrappedLines(text, indent);
    checkPage(lines.length * LINE_H + 1);
    setStyle(10, "normal", [40, 40, 40]);
    doc.text("•", ML + 3, y);
    lines.forEach((line, index) => {
      doc.text(line, indent, y);
      if (index < lines.length - 1) {
        y += LINE_H;
      }
    });
    y += LINE_H;
  };

  const estimateBulletHeight = (text, indent = ML + 7) => {
    const lines = getWrappedLines(text, indent);
    return lines.length * LINE_H + 1;
  };

  const rule = (gap = 3) => {
    y += gap;
    checkPage(2);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(ML, y, PAGE_W - MR, y);
    y += 3.5;
  };

  const sectionTitle = (title) => {
    y += 6;
    checkPage(10);
    setStyle(11, "bold", [15, 23, 42]);
    doc.text(title.toUpperCase(), ML, y);
    y += 4;
    rule(0);
  };

  const ensureSectionRoom = (minimumHeight = 24) => {
    checkPage(minimumHeight);
  };

  const estimateExperienceBlockHeight = (job) => {
    const titleHeight = 14;
    const responsibilityHeight = (job.responsibilities || []).reduce(
      (total, task) => total + (task ? estimateBulletHeight(task) : 0),
      0,
    );
    const highlightHeight = job.highlight ? getWrappedLines(job.highlight, ML).length * LINE_H + 3 : 0;
    return titleHeight + responsibilityHeight + highlightHeight;
  };

  const estimateProjectBlockHeight = (project) => {
    const titleHeight = 14;
    const detailsHeight = project.details ? LINE_H : 0;
    const responsibilityHeight = (project.responsibilities || []).reduce(
      (total, task) => total + (task ? estimateBulletHeight(task) : 0),
      0,
    );
    return titleHeight + detailsHeight + responsibilityHeight;
  };

  const estimateEducationBlockHeight = (edu) => {
    const degreeHeight = 10;
    const institutionHeight = edu.institution ? getWrappedLines(edu.institution, ML).length * LINE_H : 0;
    const relevanceHeight = edu.relevance ? getWrappedLines(edu.relevance, ML).length * LINE_H : 0;
    return degreeHeight + institutionHeight + relevanceHeight + 3;
  };

  // HEADER
  setStyle(20, "bold", [15, 23, 42]);
  doc.text(CVData.name, ML, y);
  y += 7;

  if (CVData.profession) {
    setStyle(11, "normal", [70, 85, 110]);
    doc.text(CVData.profession, ML, y);
    y += 5.5;
  }

  setStyle(9.5, "normal", [100, 100, 100]);
  const address = CVData.address;
  const phone = CVData.phone;
  const email = CVData.email;
  const linkedin = "linkedin.com/in/laurence-w-klein";
  const portfolio = CVData.portfolio;

  // First row: Address | Phone | Email
  doc.text(address, ML, y);
  let cx = ML + doc.getTextWidth(address);

  doc.text("  |  ", cx, y);
  cx += doc.getTextWidth("  |  ");
  doc.text(phone, cx, y);
  cx += doc.getTextWidth(phone);

  doc.text("  |  ", cx, y);
  cx += doc.getTextWidth("  |  ");
  setStyle(9.5, "normal", [0, 80, 180]);
  doc.text(email, cx, y);
  doc.link(cx, y - 3.5, doc.getTextWidth(email), 4.5, { url: "mailto:" + email });
  
  y += 5;
  cx = ML;

  // Second row: Web Portfolio | LinkedIn
  setStyle(9.5, "normal", [0, 80, 180]);
  doc.text(portfolio, cx, y);
  doc.link(cx, y - 3.5, doc.getTextWidth(portfolio), 4.5, { url: "https://" + CVData.portfolio });
  cx += doc.getTextWidth(portfolio);

  setStyle(9.5, "normal", [100, 100, 100]);
  doc.text("  |  ", cx, y);
  cx += doc.getTextWidth("  |  ");
  
  setStyle(9.5, "normal", [0, 80, 180]);
  doc.text(linkedin, cx, y);
  doc.link(cx, y - 3.5, doc.getTextWidth(linkedin), 4.5, { url: CVData.linkedIn });

  y += 5;
  rule(2);

  // PROFESSIONAL SUMMARY
  ensureSectionRoom(26);
  sectionTitle("Professional Summary");
  setStyle(10, "normal", [40, 40, 40]);
  write(CVData.professional_summary, ML);

  // CORE STRENGTHS
  if (CVData.core_skills && CVData.core_skills.length) {
    ensureSectionRoom(26);
    sectionTitle("Core Strengths");
    CVData.core_skills.forEach((skill) => {
      writeBullet(skill);
    });
  }

  // TECHNICAL SKILLS
  if (CVData.technical_skills && CVData.technical_skills.length) {
    ensureSectionRoom(28);
    sectionTitle("Technical Skills");
    CVData.technical_skills.forEach((skill) => {
      const [category, ...rest] = skill.split(':');
      const details = rest.join(':');
      
      y += 1;
      checkPage(8);
      setStyle(10, "bold", [15, 23, 42]);
      const label = category + ":";
      doc.text(label, ML, y);
      const labelW = doc.getTextWidth(label + " ");
      
      setStyle(10, "normal", [40, 40, 40]);
      const words = details.trim().split(' ');
      let currentLine = '';
      let isFirstLine = true;
      
      words.forEach(word => {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const testWidth = doc.getTextWidth(testLine);
        const maxWidth = isFirstLine ? CONTENT_W - labelW : CONTENT_W;
        
        if (testWidth > maxWidth && currentLine !== '') {
          doc.text(currentLine, isFirstLine ? ML + labelW : ML, y);
          y += LINE_H;
          checkPage(LINE_H);
          currentLine = word;
          isFirstLine = false;
        } else {
          currentLine = testLine;
        }
      });
      
      if (currentLine) {
        doc.text(currentLine, isFirstLine ? ML + labelW : ML, y);
      }
      y += LINE_H;
    });
  }

  // PROFESSIONAL EXPERIENCE
  if (CVData.professional_experience && CVData.professional_experience.length) {
    const firstJobHeight = estimateExperienceBlockHeight(CVData.professional_experience[0]);
    ensureSectionRoom(14 + firstJobHeight);
    sectionTitle("Professional Experience");
    CVData.professional_experience.forEach((job) => {
      y += 4;
      checkPage(estimateExperienceBlockHeight(job));
      setStyle(10.5, "bold", [15, 23, 42]);
      const titleCompany = `${job.title} - ${job.company}`;
      doc.text(titleCompany, ML, y);
      
      y += LINE_H;
      setStyle(9.5, "normal", [100, 100, 100]);
      const locationDate = `${job.location} | ${job.date}`;
      doc.text(locationDate, ML, y);

      if (job.responsibilities && job.responsibilities.length) {
        y += LINE_H;
        job.responsibilities.forEach((task) => {
          if (task) {
            writeBullet(task);
          }
        });
      }
      
      if (job.highlight) {
        y += 2;
        setStyle(10, "italic", [40, 40, 40]);
        write(job.highlight, ML);
      }
    });
  }

  // SELECTED PROJECTS
  if (CVData.selected_projects && CVData.selected_projects.length) {
    const firstProjectHeight = estimateProjectBlockHeight(CVData.selected_projects[0]);
    ensureSectionRoom(14 + firstProjectHeight);
    sectionTitle("Selected Systems Projects");
    CVData.selected_projects.forEach((project) => {
      y += 4;
      checkPage(estimateProjectBlockHeight(project));
      setStyle(10.5, "bold", [15, 23, 42]);
      doc.text(project.name, ML, y);

      if (project.details) {
        y += LINE_H;
        setStyle(9.5, "normal", [100, 100, 100]);
        doc.text(project.details, ML, y);
      }

      if (project.responsibilities && project.responsibilities.length) {
        y += LINE_H;
        project.responsibilities.forEach((task) => {
          if (task) {
            writeBullet(task);
          }
        });
      }
    });
  }

  // EDUCATION
  if (CVData.education && CVData.education.length) {
    const firstEducationHeight = estimateEducationBlockHeight(CVData.education[0]);
    ensureSectionRoom(14 + firstEducationHeight);
    sectionTitle("Education & Certifications");
    CVData.education.forEach((edu) => {
      y += 3;
      checkPage(10);
      setStyle(10.5, "bold", [15, 23, 42]);
      doc.text(edu.degree || "", ML, y);
      y += LINE_H;
      if (edu.institution) {
        setStyle(10, "normal", [70, 85, 110]);
        write(edu.institution, ML);
      }
      if (edu.relevance) {
        setStyle(10, "normal", [40, 40, 40]);
        write(edu.relevance, ML);
      }
    });
  }

  // FOOTER
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    setStyle(8, "italic", [160, 160, 160]);
    const left = "Laurence Klein - Digital Systems | Customer Experience";
    const right = "Page " + i + " of " + pageCount;
    doc.text(left, ML, PAGE_H - 8);
    doc.text(right, PAGE_W - MR - doc.getTextWidth(right), PAGE_H - 8);
  }

  doc.save("Laurence_Klein_CV.pdf");
};

export default generatePDF;